import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Alert, Button, TextField, InputAdornment, Badge,
  Tabs, Tab, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Divider, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as ApproveIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  SmartToy as AIIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '@vortx/shared-ui';
import { vortxColors } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';
import { TOUBuilder, TierEditor, GasTariffBuilder, ElectricTariffBuilder } from '@vortx/site-dna-web';
import { DocumentUploader } from '@vortx/shared-ui';
import ExtractionJobsPanel from '../../components/admin/ExtractionJobsPanel';
import PDFSourceViewer, { ConfidenceIndicator } from '../../components/admin/PDFSourceViewer';
import WholesaleMarketsTab from './WholesaleMarketsTab';

// Tab panel helper component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const TariffManagementPage = () => {
  const { currentUser, userProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-synced tab state for browser back/refresh support
  const mainTab = parseInt(searchParams.get('tab') || '0');
  const setMainTab = useCallback((value) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', value.toString());
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);
  const tariffTypeTab = searchParams.get('type') || 'electricity';
  const setTariffTypeTab = (value) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('type', value);
      return newParams;
    }, { replace: true });
  };
  const [initialTabSet, setInitialTabSet] = useState(false);

  // Data state
  const [myTariffs, setMyTariffs] = useState([]);
  const [approvedTariffs, setApprovedTariffs] = useState([]);
  const [pendingTariffs, setPendingTariffs] = useState([]);

  // Loading and error state
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUtility, setFilterUtility] = useState('');
  const [filterTariff, setFilterTariff] = useState('');

  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Selected/Editing tariff state
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [editingTariff, setEditingTariff] = useState(null);

  // AI Extraction state
  const [extractedTariffData, setExtractedTariffData] = useState(null);
  const [editingExtractedIndex, setEditingExtractedIndex] = useState(null); // Index of variant being edited
  const [pastExtractions, setPastExtractions] = useState([]);
  const [loadingExtractions, setLoadingExtractions] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null); // Track active extraction job

  // PDF viewer state for extraction review
  const [extractionPdfUri, setExtractionPdfUri] = useState(null);
  const [extractionPageCount, setExtractionPageCount] = useState(0);
  const [extractionPagesWithRates, setExtractionPagesWithRates] = useState([]);
  const [currentPdfPage, setCurrentPdfPage] = useState(1);

  const isSuperAdmin = userProfile?.is_super_admin;
  const companyId = userProfile?.company_id;

  // Set default tab to Global Library for super admins once profile loads
  useEffect(() => {
    if (isSuperAdmin && !initialTabSet) {
      setMainTab(1); // Global Library
      setInitialTabSet(true);
    }
  }, [isSuperAdmin, initialTabSet, setMainTab]);

  // Fetch tariffs from API
  const fetchTariffs = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      const token = await currentUser.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const approvedRes = await fetch(
        `${API_BASE_URL}/api/v1/admin/tariffs?status=approved&tariff_type=${tariffTypeTab}`,
        { headers }
      );
      if (!approvedRes.ok) throw new Error('Failed to fetch approved tariffs');
      const approvedData = await approvedRes.json();
      setApprovedTariffs(approvedData.rates || []);

      if (isSuperAdmin) {
        const pendingRes = await fetch(
          `${API_BASE_URL}/api/v1/admin/tariffs?status=pending&tariff_type=${tariffTypeTab}`,
          { headers }
        );
        if (pendingRes.ok) {
          const pendingData = await pendingRes.json();
          setPendingTariffs(pendingData.rates || []);
        }
      }

      const companyTariffs = (approvedData.rates || []).filter(
        t => t.company_id === companyId
      );
      setMyTariffs(companyTariffs);

    } catch (err) {
      console.error('Error fetching tariffs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser, tariffTypeTab, isSuperAdmin, companyId]);

  useEffect(() => {
    fetchTariffs();
  }, [fetchTariffs]);

  // Fetch past AI extractions
  const fetchPastExtractions = useCallback(async () => {
    if (!currentUser || !isSuperAdmin) return;

    setLoadingExtractions(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/site_dna/extractions?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setPastExtractions(data.extractions || []);
      }
    } catch (err) {
      console.error('Error fetching past extractions:', err);
    } finally {
      setLoadingExtractions(false);
    }
  }, [currentUser, isSuperAdmin]);

  // Load a specific past extraction directly into editable view
  const loadPastExtraction = useCallback(async (extractionId) => {
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/extractions/${extractionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        // Load rates directly into editable view (no separate past extraction view)
        setExtractedTariffData(data.rates || []);

        // Store PDF metadata for the viewer
        if (data.pdf_gcs_uri) {
          setExtractionPdfUri(data.pdf_gcs_uri);
          setExtractionPageCount(data.page_count || 0);
          setExtractionPagesWithRates(data.pages_with_rates || []);
        }
      }
    } catch (err) {
      console.error('Error loading extraction:', err);
      setError('Failed to load extraction details');
    }
  }, [currentUser]);

  // Fetch past extractions when AI Extraction tab is shown
  useEffect(() => {
    if (mainTab === 3 && isSuperAdmin) {
      fetchPastExtractions();
    }
  }, [mainTab, isSuperAdmin, fetchPastExtractions]);

  // Poll for job completion and auto-refresh extractions
  useEffect(() => {
    if (!currentJobId || !currentUser) return;

    const pollJobStatus = async () => {
      try {
        const token = await currentUser.getIdToken();
        const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/solver/jobs/${currentJobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.ok) {
          const job = await res.json();

          if (job.status === 'complete') {
            // Job finished - refresh extractions list and load result
            setCurrentJobId(null);
            await fetchPastExtractions();

            // Auto-load the completed extraction if we have the extraction_id
            if (job.extraction_id) {
              await loadPastExtraction(job.extraction_id);
            }
          } else if (job.status === 'failed') {
            setCurrentJobId(null);
            setError(`Extraction failed: ${job.error || 'Unknown error'}`);
          }
        }
      } catch (err) {
        console.error('Error polling job status:', err);
      }
    };

    // Poll every 3 seconds
    const interval = setInterval(pollJobStatus, 3000);
    pollJobStatus(); // Also poll immediately

    return () => clearInterval(interval);
  }, [currentJobId, currentUser, fetchPastExtractions, loadPastExtraction]);

  // Handle tariff approval
  const handleApprove = async (tariff) => {
    if (!currentUser || !isSuperAdmin) return;

    setApproving(tariff.id);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/tariffs/pending/${tariff.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (res.ok) {
        setSuccess('Tariff approved and added to Flux Rate Database');
        await fetchTariffs();
        setSelectedTariff(null);
        setEditingTariff(null);
      } else {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to approve tariff (${res.status})`);
      }
    } catch (err) {
      console.error('Error approving tariff:', err);
      setError(err.message);
    } finally {
      setApproving(null);
    }
  };

  // Handle save and approve (for pending tariffs)
  const handleSaveAndApprove = async () => {
    if (!currentUser || !isSuperAdmin || !editingTariff) return;

    setSaving(true);
    try {
      const token = await currentUser.getIdToken();

      // First save any edits
      const saveRes = await fetch(`${API_BASE_URL}/api/v1/admin/tariffs/${editingTariff.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editingTariff.name,
          rate_data: editingTariff.rate_data
        })
      });

      if (!saveRes.ok) {
        const errorData = await saveRes.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save tariff');
      }

      // Then approve it
      const approveRes = await fetch(`${API_BASE_URL}/api/v1/admin/tariffs/pending/${editingTariff.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!approveRes.ok) {
        const errorData = await approveRes.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to approve tariff');
      }

      setSuccess('Tariff approved and added to Flux Rate Database');
      await fetchTariffs();
      setSelectedTariff(null);
      setEditingTariff(null);
    } catch (err) {
      console.error('Error saving and approving tariff:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle save tariff
  const handleSave = async () => {
    if (!currentUser || !editingTariff) return;

    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      const endpoint = editingTariff.status === 'pending'
        ? `/api/v1/admin/tariffs/pending/${editingTariff.id}`
        : `/api/v1/admin/tariffs/${editingTariff.id}`;

      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editingTariff.name,
          utility_name: editingTariff.utility_name,
          rate_schedule_name: editingTariff.rate_schedule_name,
          rate_data: editingTariff.rate_data,
        })
      });

      if (res.ok) {
        await fetchTariffs();
        setSelectedTariff(null);
        setEditingTariff(null);
      } else {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save tariff');
      }
    } catch (err) {
      console.error('Error saving tariff:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle delete tariff
  const handleDelete = async () => {
    if (!currentUser || !selectedTariff) return;
    if (!window.confirm(`Delete tariff "${selectedTariff.name}"? This cannot be undone.`)) return;

    try {
      const token = await currentUser.getIdToken();
      const endpoint = selectedTariff.status === 'pending'
        ? `/api/v1/admin/tariffs/pending/${selectedTariff.id}`
        : `/api/v1/admin/tariffs/${selectedTariff.id}`;

      const res = await fetch(endpoint, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchTariffs();
        setSelectedTariff(null);
        setEditingTariff(null);
      } else {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete tariff');
      }
    } catch (err) {
      console.error('Error deleting tariff:', err);
      setError(err.message);
    }
  };

  // Open tariff for viewing/editing
  const handleTariffClick = (tariff) => {
    setSelectedTariff(tariff);
    setEditingTariff({ ...tariff });
  };

  // Close dialog
  const handleCloseDialog = () => {
    setSelectedTariff(null);
    setEditingTariff(null);
  };

  // Get unique utilities and tariff schedules for filters
  const utilities = useMemo(() => {
    const utilitySet = new Set(approvedTariffs.map(t => t.utility_name).filter(Boolean));
    return Array.from(utilitySet).sort();
  }, [approvedTariffs]);

  const tariffSchedules = useMemo(() => {
    if (!filterUtility) return [];
    const scheduleSet = new Set(
      approvedTariffs
        .filter(t => t.utility_name === filterUtility)
        .map(t => t.rate_schedule_name)
        .filter(Boolean)
    );
    return Array.from(scheduleSet).sort();
  }, [approvedTariffs, filterUtility]);

  // Filter tariffs based on search term and filters
  const filterTariffs = useCallback((tariffs) => {
    let filtered = tariffs;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        (t.utility_name || '').toLowerCase().includes(term) ||
        (t.rate_schedule_name || t.name || '').toLowerCase().includes(term)
      );
    }

    if (filterUtility) {
      filtered = filtered.filter(t => t.utility_name === filterUtility);
    }

    if (filterTariff) {
      filtered = filtered.filter(t => t.rate_schedule_name === filterTariff);
    }

    return filtered;
  }, [searchTerm, filterUtility, filterTariff]);

  const filteredMyTariffs = useMemo(() => filterTariffs(myTariffs), [filterTariffs, myTariffs]);
  const filteredApprovedTariffs = useMemo(() => filterTariffs(approvedTariffs), [filterTariffs, approvedTariffs]);
  const filteredPendingTariffs = useMemo(() => filterTariffs(pendingTariffs), [filterTariffs, pendingTariffs]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '-';
    }
  };

  // Render tariff table
  const renderTariffTable = (tariffs, showApproveAction = false) => {
    const isElectric = tariffTypeTab === 'electricity';

    if (tariffs.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {showApproveAction
              ? 'No tariffs pending approval'
              : mainTab === 0
                ? 'No custom tariffs yet'
                : 'No tariffs found'}
          </Typography>
        </Paper>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Utility</TableCell>
              <TableCell>Tariff Name</TableCell>
              {isElectric && <TableCell>Voltage</TableCell>}
              {isElectric && <TableCell>Option</TableCell>}
              <TableCell>Mode</TableCell>
              <TableCell>Status</TableCell>
              {!showApproveAction && <TableCell>Approved</TableCell>}
              {showApproveAction && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {tariffs.map((tariff) => (
              <TableRow
                key={tariff.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => handleTariffClick(tariff)}
              >
                <TableCell>{tariff.utility_name || '-'}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {tariff.rate_schedule_name || tariff.name || '-'}
                  </Typography>
                </TableCell>
                {isElectric && (
                  <TableCell>{tariff.rate_data?.voltage_level || '-'}</TableCell>
                )}
                {isElectric && (
                  <TableCell>{tariff.rate_data?.option || '-'}</TableCell>
                )}
                <TableCell>
                  <Chip
                    label={tariff.rate_mode || 'flat'}
                    size="small"
                    sx={{
                      bgcolor: alpha(vortxColors.gold, 0.2),
                      color: vortxColors.gold
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={tariff.status || 'unknown'}
                    size="small"
                    color={tariff.status ? getStatusColor(tariff.status) : 'default'}
                  />
                </TableCell>
                {!showApproveAction && (
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                    {formatDate(tariff.approved_at || tariff.updated_at)}
                  </TableCell>
                )}
                {showApproveAction && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {isSuperAdmin && tariff.status === 'pending' && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={approving === tariff.id ? <CircularProgress size={16} /> : <ApproveIcon />}
                        onClick={() => handleApprove(tariff)}
                        disabled={approving === tariff.id}
                      >
                        Approve
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render tariff detail/edit dialog
  const renderTariffDialog = () => {
    if (!selectedTariff || !editingTariff) return null;

    const isElectric = editingTariff.tariff_type === 'electricity' || !editingTariff.tariff_type;
    const isGas = editingTariff.tariff_type === 'natural_gas' || editingTariff.tariff_type === 'gas';
    // Treat 'native_8760' as TOU - these tariffs have TOU data structure from AI extraction
    const hasTOUData = editingTariff.rate_data?.touData?.seasons?.length > 0;
    const isTOU = editingTariff.rate_mode === 'tou' || editingTariff.rate_mode === 'native_8760' || hasTOUData;
    const isTiered = editingTariff.rate_mode === 'tiered';

    return (
      <Dialog
        open={!!selectedTariff}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">{selectedTariff.name || 'Tariff Details'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedTariff.utility_name} • {selectedTariff.rate_schedule_name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isSuperAdmin && (
              <Tooltip title="Delete Tariff">
                <IconButton onClick={handleDelete} color="error" size="small">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                fullWidth
                value={editingTariff.name || ''}
                onChange={(e) => setEditingTariff(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Utility"
                fullWidth
                value={editingTariff.utility_name || ''}
                onChange={(e) => setEditingTariff(prev => ({ ...prev, utility_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Tariff Schedule"
                fullWidth
                value={editingTariff.rate_schedule_name || ''}
                onChange={(e) => setEditingTariff(prev => ({ ...prev, rate_schedule_name: e.target.value }))}
              />
            </Grid>
            {isElectric && (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Voltage Level"
                    fullWidth
                    value={editingTariff.rate_data?.voltage_level || ''}
                    onChange={(e) => setEditingTariff(prev => ({
                      ...prev,
                      rate_data: { ...prev.rate_data, voltage_level: e.target.value }
                    }))}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Option"
                    fullWidth
                    value={editingTariff.rate_data?.option || ''}
                    onChange={(e) => setEditingTariff(prev => ({
                      ...prev,
                      rate_data: { ...prev.rate_data, option: e.target.value }
                    }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Rate Mode</InputLabel>
                    <Select
                      value={editingTariff.rate_mode === 'native_8760' ? 'tou' : (editingTariff.rate_mode || 'tou')}
                      label="Rate Mode"
                      onChange={(e) => setEditingTariff(prev => ({ ...prev, rate_mode: e.target.value }))}
                    >
                      <MenuItem value="simple">Simple (Blended)</MenuItem>
                      <MenuItem value="tiered">Tiered (Block)</MenuItem>
                      <MenuItem value="tou">Time-of-Use (TOU)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* TOU Configuration */}
            {isElectric && isTOU && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <TOUBuilder
                  touData={(() => {
                    const rateData = editingTariff.rate_data || {};
                    const touData = rateData.touData || {};

                    return {
                      seasons: touData.seasons || rateData.seasons || [],
                      periods: touData.periods || rateData.periods || [],
                      windows: touData.windows || rateData.windows || [],
                      schedule: touData.schedule || rateData.schedule,
                      fixedMonthlyFee: touData.fixedMonthlyFee || touData.fixedMonthlyCharge || rateData.fixedMonthlyCharge || '',
                      facilityDemand: touData.facilityDemand || rateData.facilityDemand || ''
                    };
                  })()}
                  onChange={(newTouData) => setEditingTariff(prev => ({
                    ...prev,
                    rate_data: {
                      ...prev.rate_data,
                      touData: newTouData,
                      seasons: newTouData.seasons,
                      periods: newTouData.periods,
                      windows: newTouData.windows,
                      fixedMonthlyCharge: newTouData.fixedMonthlyFee,
                      facilityDemand: newTouData.facilityDemand
                    }
                  }))}
                  readOnly={false}
                />
              </Grid>
            )}

            {/* Tiered Configuration */}
            {isElectric && isTiered && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.secondary' }}>
                  Tiered Energy Rates
                </Typography>
                <TierEditor
                  tiers={editingTariff.rate_data?.energyTiers || editingTariff.rate_data?.tiers || []}
                  onChange={(tiers) => setEditingTariff(prev => ({
                    ...prev,
                    rate_data: { ...prev.rate_data, energyTiers: tiers, tiers: tiers }
                  }))}
                />
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Demand Charges
                  </Typography>
                  <TextField
                    label="Flat Demand Rate ($/kW)"
                    type="number"
                    fullWidth
                    value={editingTariff.rate_data?.blendedDemandRate || ''}
                    onChange={(e) => setEditingTariff(prev => ({
                      ...prev,
                      rate_data: { ...prev.rate_data, blendedDemandRate: e.target.value }
                    }))}
                    helperText="Applied alongside tiered energy rates"
                    sx={{ maxWidth: 300 }}
                  />
                </Box>
              </Grid>
            )}

            {/* Gas Tariff Configuration */}
            {isGas && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>Gas Tariff Configuration</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Fixed Customer Charge"
                    type="number"
                    value={editingTariff.rate_data?.customer_charge_per_day || ''}
                    onChange={(e) => setEditingTariff(prev => ({
                      ...prev,
                      rate_data: { ...prev.rate_data, customer_charge_per_day: parseFloat(e.target.value) || 0 }
                    }))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: <InputAdornment position="end">/day</InputAdornment>
                    }}
                    fullWidth
                    sx={{ maxWidth: 300 }}
                  />

                  <Typography variant="subtitle2" sx={{ mt: 2, color: 'text.secondary' }}>
                    Usage Tiers
                  </Typography>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tier</TableCell>
                          <TableCell>Up To (therms/month)</TableCell>
                          <TableCell>Rate ($/therm)</TableCell>
                          <TableCell width={60}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(editingTariff.rate_data?.tiers || []).map((tier, index) => (
                          <TableRow key={index}>
                            <TableCell>Tier {tier.tier_number || index + 1}</TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={tier.max_usage_therms || ''}
                                onChange={(e) => {
                                  const newTiers = [...(editingTariff.rate_data?.tiers || [])];
                                  newTiers[index] = { ...newTiers[index], max_usage_therms: e.target.value ? Number(e.target.value) : null };
                                  setEditingTariff(prev => ({
                                    ...prev,
                                    rate_data: { ...prev.rate_data, tiers: newTiers }
                                  }));
                                }}
                                placeholder="Unlimited"
                                size="small"
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={tier.rate_per_therm || ''}
                                onChange={(e) => {
                                  const newTiers = [...(editingTariff.rate_data?.tiers || [])];
                                  newTiers[index] = { ...newTiers[index], rate_per_therm: Number(e.target.value) };
                                  setEditingTariff(prev => ({
                                    ...prev,
                                    rate_data: { ...prev.rate_data, tiers: newTiers }
                                  }));
                                }}
                                size="small"
                                fullWidth
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const newTiers = (editingTariff.rate_data?.tiers || []).filter((_, i) => i !== index);
                                  setEditingTariff(prev => ({
                                    ...prev,
                                    rate_data: { ...prev.rate_data, tiers: newTiers }
                                  }));
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const tiers = editingTariff.rate_data?.tiers || [];
                      const newTier = {
                        tier_number: tiers.length + 1,
                        max_usage_therms: null,
                        rate_per_therm: 0
                      };
                      setEditingTariff(prev => ({
                        ...prev,
                        rate_data: { ...prev.rate_data, tiers: [...tiers, newTier] }
                      }));
                    }}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Add Tier
                  </Button>
                </Box>
              </Grid>
            )}

            {/* Simple/Blended Rate */}
            {isElectric && editingTariff.rate_mode === 'simple' && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>Simple/Blended Rate</Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Use simple annual averages for energy and demand charges.
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      label="Blended Energy Rate ($/kWh)"
                      type="number"
                      fullWidth
                      value={editingTariff.rate_data?.blendedEnergyRate || editingTariff.rate_data?.energy_rate || ''}
                      onChange={(e) => setEditingTariff(prev => ({
                        ...prev,
                        rate_data: { ...prev.rate_data, blendedEnergyRate: parseFloat(e.target.value) || 0 }
                      }))}
                      inputProps={{ step: 0.001 }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      label="Blended Demand Rate ($/kW)"
                      type="number"
                      fullWidth
                      value={editingTariff.rate_data?.blendedDemandRate || editingTariff.rate_data?.demand_rate || ''}
                      onChange={(e) => setEditingTariff(prev => ({
                        ...prev,
                        rate_data: { ...prev.rate_data, blendedDemandRate: parseFloat(e.target.value) || 0 }
                      }))}
                      inputProps={{ step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      label="Fixed Charge ($/month)"
                      type="number"
                      fullWidth
                      value={editingTariff.rate_data?.fixed_charge || ''}
                      onChange={(e) => setEditingTariff(prev => ({
                        ...prev,
                        rate_data: { ...prev.rate_data, fixed_charge: parseFloat(e.target.value) || 0 }
                      }))}
                      inputProps={{ step: 0.01 }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Flat Rate - fallback for old data */}
            {isElectric && !isTOU && !isTiered && editingTariff.rate_mode !== 'simple' && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>Flat Rate</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      label="Energy Rate ($/kWh)"
                      type="number"
                      fullWidth
                      value={editingTariff.rate_data?.energy_rate || ''}
                      onChange={(e) => setEditingTariff(prev => ({
                        ...prev,
                        rate_data: { ...prev.rate_data, energy_rate: parseFloat(e.target.value) || 0 }
                      }))}
                      inputProps={{ step: 0.001 }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      label="Demand Rate ($/kW)"
                      type="number"
                      fullWidth
                      value={editingTariff.rate_data?.demand_rate || ''}
                      onChange={(e) => setEditingTariff(prev => ({
                        ...prev,
                        rate_data: { ...prev.rate_data, demand_rate: parseFloat(e.target.value) || 0 }
                      }))}
                      inputProps={{ step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      label="Fixed Charge ($/month)"
                      type="number"
                      fullWidth
                      value={editingTariff.rate_data?.fixed_charge || ''}
                      onChange={(e) => setEditingTariff(prev => ({
                        ...prev,
                        rate_data: { ...prev.rate_data, fixed_charge: parseFloat(e.target.value) || 0 }
                      }))}
                      inputProps={{ step: 0.01 }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {/* Show "Save and Approve" for pending tariffs, "Save Changes" for approved */}
          {selectedTariff.status === 'pending' || selectedTariff.status === 'pending_approval' ? (
            isSuperAdmin && (
              <Button
                variant="contained"
                onClick={handleSaveAndApprove}
                disabled={saving || approving === selectedTariff.id}
                sx={{ bgcolor: '#2ecc71', '&:hover': { bgcolor: '#27ae60' } }}
              >
                {saving ? <CircularProgress size={20} /> : 'Save and Approve'}
              </Button>
            )
          ) : (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <EditIcon />}
              sx={{ bgcolor: vortxColors.gold, color: '#000', '&:hover': { bgcolor: alpha(vortxColors.gold, 0.8) } }}
            >
              Save Changes
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  if (loading && !myTariffs.length && !approvedTariffs.length && !pendingTariffs.length) {
    return (
      <AdminLayout title="Tariff Management">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Tariff Management" maxWidth={editingExtractedIndex !== null ? "xl" : "lg"}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tariff Type Sub-tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tariffTypeTab === 'electricity' ? 0 : 1}
          onChange={(e, v) => setTariffTypeTab(v === 0 ? 'electricity' : 'natural_gas')}
        >
          <Tab label="Electric Tariffs" />
          <Tab label="Gas Tariffs" />
        </Tabs>
      </Box>

      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={mainTab} onChange={(e, v) => setMainTab(v)}>
          <Tab label="My Tariffs" />
          <Tab label="Global Library" />
          {isSuperAdmin && (
            <Tab
              label={
                <Badge
                  badgeContent={pendingTariffs.length}
                  color="warning"
                  sx={{ '& .MuiBadge-badge': { right: -12, top: 8 } }}
                >
                  Pending Approval
                </Badge>
              }
            />
          )}
          {isSuperAdmin && (
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AIIcon fontSize="small" />
                  AI Extraction
                </Box>
              }
            />
          )}
          {isSuperAdmin && (
            <Tab label="Wholesale Markets" />
          )}
        </Tabs>
      </Box>

      {/* Filters - hide on AI Extraction and Wholesale Markets tabs */}
      {mainTab !== 3 && mainTab !== 4 && (
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by utility or tariff name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Utility</InputLabel>
          <Select
            value={filterUtility}
            label="Utility"
            onChange={(e) => {
              setFilterUtility(e.target.value);
              setFilterTariff(''); // Reset tariff when utility changes
            }}
          >
            <MenuItem value="">All Utilities</MenuItem>
            {utilities.map(u => (
              <MenuItem key={u} value={u}>{u}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }} disabled={!filterUtility}>
          <InputLabel>Schedule</InputLabel>
          <Select
            value={filterTariff}
            label="Schedule"
            onChange={(e) => setFilterTariff(e.target.value)}
          >
            <MenuItem value="">All Schedules</MenuItem>
            {tariffSchedules.map(t => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Chip
          label={`${filteredApprovedTariffs.length} tariff${filteredApprovedTariffs.length !== 1 ? 's' : ''}`}
          size="small"
          sx={{ bgcolor: alpha(vortxColors.gold, 0.2), color: vortxColors.gold }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={fetchTariffs}
          disabled={loading}
        >
          Refresh
        </Button>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateDialog(true)}
            sx={{
              bgcolor: vortxColors.gold,
              color: '#000',
              '&:hover': { bgcolor: alpha(vortxColors.gold, 0.8) }
            }}
          >
            Create {tariffTypeTab === 'electricity' ? 'Electric' : 'Gas'} Tariff
          </Button>
        )}
      </Box>
      )}

      {/* Tab Content */}
      <TabPanel value={mainTab} index={0}>
        {renderTariffTable(filteredMyTariffs)}
      </TabPanel>

      <TabPanel value={mainTab} index={1}>
        {renderTariffTable(filteredApprovedTariffs)}
      </TabPanel>

      {isSuperAdmin && (
        <TabPanel value={mainTab} index={2}>
          {renderTariffTable(filteredPendingTariffs, true)}
        </TabPanel>
      )}

      {/* AI Extraction Tab - Super Admin Only */}
      {isSuperAdmin && (
        <TabPanel value={mainTab} index={3}>
          {!extractedTariffData ? (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>AI Tariff Extraction</strong><br />
                Upload a tariff PDF and the AI will automatically analyze its structure
                and extract rates. No pre-configuration required.
              </Alert>
              <DocumentUploader
                defaultDocumentType="tariff_schedule"
                allowedDocumentTypes={['tariff_schedule', 'rate_book']}
                title="Upload Tariff PDF"
                description="AI will extract ALL voltage levels and options automatically"
                acceptedFileTypes=".pdf"
                onExtractionComplete={(job) => {
                  console.log('Unified extraction complete:', job);
                  const data = job.extracted_data || job;
                  if (data.variants && data.variants.length > 0) {
                    setExtractedTariffData(data.variants);
                    setSuccess(`Extracted: ${data.utility_name || ''} ${data.tariff_code || ''}`);
                    fetchPastExtractions();
                    if (data.pdf_gcs_uri) {
                      setExtractionPdfUri(data.pdf_gcs_uri);
                      setExtractionPageCount(data.page_count || 0);
                      setExtractionPagesWithRates(data.pages_with_rates || []);
                    }
                  }
                }}
                onError={(err) => {
                  setError(err.message || 'Extraction failed');
                }}
              />

              {/* Background Jobs Panel - shows active/stuck jobs with delete buttons */}
              <Box sx={{ mt: 3 }}>
                <ExtractionJobsPanel
                  newJobId={currentJobId}
                  onJobComplete={(job) => {
                    console.log('Job completed via panel:', job);
                    if (job.extraction_id) {
                      loadPastExtraction(job.extraction_id);
                    }
                    fetchPastExtractions();
                  }}
                  accentColor={vortxColors.gold}
                />
              </Box>

              {/* Past Extractions History */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, color: vortxColors.gold }}>
                  Past Extractions
                </Typography>

                {loadingExtractions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : pastExtractions.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">No past extractions yet</Typography>
                  </Paper>
                ) : (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>PDF File</TableCell>
                          <TableCell>Utility</TableCell>
                          <TableCell>Tariff</TableCell>
                          <TableCell>Variants</TableCell>
                          <TableCell>Voltage Levels</TableCell>
                          <TableCell width={60}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pastExtractions.map((extraction) => (
                          <TableRow
                            key={extraction.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => loadPastExtraction(extraction.id)}
                          >
                            <TableCell>
                              {new Date(extraction.extraction_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {extraction.source_pdf_name}
                              </Typography>
                            </TableCell>
                            <TableCell>{extraction.utility_name}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                {(extraction.tariff_codes && extraction.tariff_codes.length > 0
                                  ? extraction.tariff_codes
                                  : [extraction.tariff_code]
                                ).slice(0, 2).map((code, i) => (
                                  <Chip key={`${extraction.id}-tc-${i}`} label={code} size="small" sx={{ bgcolor: alpha(vortxColors.gold, 0.2), color: vortxColors.gold }} />
                                ))}
                                {(extraction.tariff_codes || []).length > 2 && (
                                  <Chip
                                    label={`+${extraction.tariff_codes.length - 2} more`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ color: vortxColors.gold, borderColor: alpha(vortxColors.gold, 0.3) }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {extraction.rates_count} rates
                              {extraction.tariffs_extracted > 1 && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  from {extraction.tariffs_extracted} tariffs
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {(extraction.voltage_levels_found || []).slice(0, 3).map((v, i) => (
                                  <Chip key={`${extraction.id}-${v}-${i}`} label={v} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="Delete extraction">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={async () => {
                                    if (!window.confirm(`Delete extraction "${extraction.utility_name} ${extraction.tariff_code}"? This cannot be undone.`)) return;
                                    try {
                                      const token = await currentUser.getIdToken();
                                      const res = await fetch(`${API_BASE_URL}/api/extractions/${extraction.id}`, {
                                        method: 'DELETE',
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      if (res.ok) {
                                        setSuccess('Extraction deleted');
                                        fetchPastExtractions();
                                      } else {
                                        const err = await res.json();
                                        setError(err.error || 'Failed to delete extraction');
                                      }
                                    } catch (err) {
                                      setError('Failed to delete extraction');
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Box>
          ) : editingExtractedIndex !== null ? (
                // Detail editor for a single variant - with side-by-side PDF viewer
                <Box>
                  <Button
                    startIcon={<CloseIcon />}
                    onClick={() => setEditingExtractedIndex(null)}
                    sx={{ mb: 2 }}
                  >
                    Back to List
                  </Button>

                  {/* Side-by-side layout: Editor on left, PDF on right */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    {/* Editor Panel - takes more space */}
                    <Paper sx={{ p: 3, flex: extractionPdfUri ? '1 1 60%' : '1 1 100%', minWidth: 0, maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                          <Typography variant="h6" sx={{ color: vortxColors.gold }}>
                            Edit: {extractedTariffData[editingExtractedIndex]?.name || extractedTariffData[editingExtractedIndex]?.tariff_code}
                          </Typography>
                          {/* Overall confidence indicator */}
                          {extractedTariffData[editingExtractedIndex]?.confidence && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">Extraction Confidence:</Typography>
                              <ConfidenceIndicator
                                confidence={extractedTariffData[editingExtractedIndex]?.confidence?.periods || 0}
                                label="Periods"
                              />
                            </Box>
                          )}
                        </Box>

                        {/* Source page indicator */}
                        {extractedTariffData[editingExtractedIndex]?.primary_source_page && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>Source:</strong> Page {extractedTariffData[editingExtractedIndex].primary_source_page}
                            {extractedTariffData[editingExtractedIndex]?.source_pages?.length > 1 && (
                              <> (also pages: {extractedTariffData[editingExtractedIndex].source_pages.filter(p => p !== extractedTariffData[editingExtractedIndex].primary_source_page).join(', ')})</>
                            )}
                            <Button
                              size="small"
                              sx={{ ml: 2 }}
                              onClick={() => setCurrentPdfPage(extractedTariffData[editingExtractedIndex].primary_source_page)}
                            >
                              Go to Page
                            </Button>
                          </Alert>
                        )}

                        {/* Metadata Fields */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Tariff Name"
                              fullWidth
                              value={extractedTariffData[editingExtractedIndex]?.name || ''}
                              onChange={(e) => {
                                const updated = [...extractedTariffData];
                                updated[editingExtractedIndex] = { ...updated[editingExtractedIndex], name: e.target.value };
                                setExtractedTariffData(updated);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Utility"
                              fullWidth
                              value={extractedTariffData[editingExtractedIndex]?.utility_name || ''}
                              onChange={(e) => {
                                const updated = [...extractedTariffData];
                                updated[editingExtractedIndex] = { ...updated[editingExtractedIndex], utility_name: e.target.value };
                                setExtractedTariffData(updated);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Box sx={{ position: 'relative' }}>
                              <TextField
                                label="Tariff Code"
                                fullWidth
                                value={extractedTariffData[editingExtractedIndex]?.tariff_code || ''}
                                onChange={(e) => {
                                  const updated = [...extractedTariffData];
                                  updated[editingExtractedIndex] = { ...updated[editingExtractedIndex], tariff_code: e.target.value };
                                  setExtractedTariffData(updated);
                                }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Voltage Level"
                              fullWidth
                              value={extractedTariffData[editingExtractedIndex]?.voltage_level || ''}
                              onChange={(e) => {
                                const updated = [...extractedTariffData];
                                updated[editingExtractedIndex] = { ...updated[editingExtractedIndex], voltage_level: e.target.value };
                                setExtractedTariffData(updated);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Option"
                              fullWidth
                              value={extractedTariffData[editingExtractedIndex]?.option || ''}
                              onChange={(e) => {
                                const updated = [...extractedTariffData];
                                updated[editingExtractedIndex] = { ...updated[editingExtractedIndex], option: e.target.value };
                                setExtractedTariffData(updated);
                              }}
                            />
                          </Grid>

                          {/* Demand Applicability */}
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Min Demand (kW)"
                              type="number"
                              fullWidth
                              value={extractedTariffData[editingExtractedIndex]?.demand_applicability?.min_kw ?? ''}
                              onChange={(e) => {
                                const updated = [...extractedTariffData];
                                const current = updated[editingExtractedIndex]?.demand_applicability || {};
                                updated[editingExtractedIndex] = {
                                  ...updated[editingExtractedIndex],
                                  demand_applicability: { ...current, min_kw: e.target.value ? Number(e.target.value) : null }
                                };
                                setExtractedTariffData(updated);
                              }}
                              helperText="Minimum demand requirement"
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Max Demand (kW)"
                              type="number"
                              fullWidth
                              value={extractedTariffData[editingExtractedIndex]?.demand_applicability?.max_kw ?? ''}
                              onChange={(e) => {
                                const updated = [...extractedTariffData];
                                const current = updated[editingExtractedIndex]?.demand_applicability || {};
                                updated[editingExtractedIndex] = {
                                  ...updated[editingExtractedIndex],
                                  demand_applicability: { ...current, max_kw: e.target.value ? Number(e.target.value) : null }
                                };
                                setExtractedTariffData(updated);
                              }}
                              helperText="Maximum demand limit"
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Applicability Description"
                              fullWidth
                              value={extractedTariffData[editingExtractedIndex]?.demand_applicability?.description || ''}
                              onChange={(e) => {
                                const updated = [...extractedTariffData];
                                const current = updated[editingExtractedIndex]?.demand_applicability || {};
                                updated[editingExtractedIndex] = {
                                  ...updated[editingExtractedIndex],
                                  demand_applicability: { ...current, description: e.target.value }
                                };
                                setExtractedTariffData(updated);
                              }}
                              helperText="e.g. Demand >= 500 kW"
                            />
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        {/* Fixed charges with confidence */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                label="Fixed Monthly Fee ($/mo)"
                                type="number"
                                fullWidth
                                value={extractedTariffData[editingExtractedIndex]?.fixedMonthlyFee || ''}
                                onChange={(e) => {
                                  const updated = [...extractedTariffData];
                                  updated[editingExtractedIndex] = { ...updated[editingExtractedIndex], fixedMonthlyFee: e.target.value };
                                  setExtractedTariffData(updated);
                                }}
                              />
                              <ConfidenceIndicator
                                confidence={extractedTariffData[editingExtractedIndex]?.confidence?.fixedMonthlyFee}
                                label="Fixed Fee"
                                compact
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                label="Facility Demand ($/kW)"
                                type="number"
                                fullWidth
                                value={extractedTariffData[editingExtractedIndex]?.facilityDemand || ''}
                                onChange={(e) => {
                                  const updated = [...extractedTariffData];
                                  updated[editingExtractedIndex] = { ...updated[editingExtractedIndex], facilityDemand: e.target.value };
                                  setExtractedTariffData(updated);
                                }}
                              />
                              <ConfidenceIndicator
                                confidence={extractedTariffData[editingExtractedIndex]?.confidence?.facilityDemand}
                                label="Facility Demand"
                                compact
                              />
                            </Box>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        {/* TOU Builder for editing */}
                        <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.secondary' }}>
                          Time-of-Use Schedule & Rates
                        </Typography>
                        <TOUBuilder
                          touData={{
                            seasons: extractedTariffData[editingExtractedIndex]?.seasons || [],
                            periods: extractedTariffData[editingExtractedIndex]?.periods || [],
                            windows: extractedTariffData[editingExtractedIndex]?.windows || [],
                            schedule: extractedTariffData[editingExtractedIndex]?.schedule || null,
                            fixedMonthlyFee: extractedTariffData[editingExtractedIndex]?.fixedMonthlyFee || '',
                            facilityDemand: extractedTariffData[editingExtractedIndex]?.facilityDemand || ''
                          }}
                          onChange={(newTouData) => {
                            const updated = [...extractedTariffData];
                            updated[editingExtractedIndex] = {
                              ...updated[editingExtractedIndex],
                              seasons: newTouData.seasons,
                              periods: newTouData.periods,
                              windows: newTouData.windows,
                              schedule: newTouData.schedule,
                              fixedMonthlyFee: newTouData.fixedMonthlyFee,
                              facilityDemand: newTouData.facilityDemand
                            };
                            setExtractedTariffData(updated);
                          }}
                          readOnly={false}
                        />

                        <Divider sx={{ my: 3 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                          <Button variant="outlined" onClick={() => setEditingExtractedIndex(null)}>
                            Done Editing
                          </Button>
                        </Box>
                      </Paper>

                    {/* PDF Viewer Panel - Sticky on right, narrower */}
                    {extractionPdfUri && (
                      <Box sx={{ flex: '0 0 40%', minWidth: 350, position: 'sticky', top: 80, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 100px)' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                          Source PDF - Compare with extracted values
                        </Typography>
                        <PDFSourceViewer
                          pdfGcsUri={extractionPdfUri}
                          pageCount={extractionPageCount}
                          pagesWithRates={extractionPagesWithRates}
                          currentPage={extractedTariffData[editingExtractedIndex]?.primary_source_page || currentPdfPage}
                          onPageChange={setCurrentPdfPage}
                          height="calc(100vh - 180px)"
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
          ) : (
                <Box>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    <strong>Extraction Complete!</strong> Found {extractedTariffData.length} rate variant(s).
                    Click a row to edit details, then select which ones to save.
                  </Alert>

                  {/* Multi-variant table */}
                  <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          onClick={() => {
                            setExtractedTariffData(null);
                            setExtractionPdfUri(null);
                            setExtractionPageCount(0);
                            setExtractionPagesWithRates([]);
                          }}
                          startIcon={<CloseIcon />}
                        >
                          Back to Extractions
                        </Button>
                        <Typography variant="h6" sx={{ color: vortxColors.gold }}>
                          Extracted Rate Variants
                        </Typography>
                      </Box>
                      {extractionPageCount > 0 && (
                        <Chip
                          label={`${extractionPageCount} pages | ${extractionPagesWithRates.length} with rate tables`}
                          size="small"
                          sx={{ bgcolor: alpha(vortxColors.teal, 0.1), color: vortxColors.teal }}
                        />
                      )}
                    </Box>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <input
                                type="checkbox"
                                checked={extractedTariffData.every(r => r._selected)}
                                onChange={(e) => {
                                  setExtractedTariffData(extractedTariffData.map(r => ({
                                    ...r,
                                    _selected: e.target.checked
                                  })));
                                }}
                              />
                            </TableCell>
                            <TableCell>Tariff Name</TableCell>
                            <TableCell>Voltage</TableCell>
                            <TableCell>Option</TableCell>
                            <TableCell>Demand Range</TableCell>
                            <TableCell>Periods</TableCell>
                            <TableCell>Confidence</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Saved</TableCell>
                            <TableCell width={80}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {extractedTariffData.map((rate, index) => (
                            <TableRow
                              key={index}
                              hover
                              sx={{ cursor: 'pointer' }}
                              onClick={() => setEditingExtractedIndex(index)}
                            >
                              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={rate._selected || false}
                                  onChange={(e) => {
                                    const updated = [...extractedTariffData];
                                    updated[index] = { ...updated[index], _selected: e.target.checked };
                                    setExtractedTariffData(updated);
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {rate.name || `${rate.utility_name} ${rate.tariff_code}`}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={rate.voltage_level || 'N/A'} size="small" />
                              </TableCell>
                              <TableCell>{rate.option || '-'}</TableCell>
                              <TableCell>
                                {(() => {
                                  const da = rate.demand_applicability;
                                  if (!da) return '-';
                                  if (da.min_kw != null && da.max_kw != null) return `${da.min_kw} - ${da.max_kw} kW`;
                                  if (da.min_kw != null) return `${da.min_kw} kW+`;
                                  if (da.max_kw != null) return `≤ ${da.max_kw} kW`;
                                  return da.description || '-';
                                })()}
                              </TableCell>
                              <TableCell>{rate.periods?.length || 0} periods</TableCell>
                              <TableCell>
                                <ConfidenceIndicator confidence={rate.confidence?.periods} label="Overall" />
                              </TableCell>
                              <TableCell>
                                {rate.primary_source_page ? (
                                  <Chip
                                    label={`Page ${rate.primary_source_page}`}
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCurrentPdfPage(rate.primary_source_page);
                                    }}
                                    sx={{
                                      cursor: 'pointer',
                                      bgcolor: alpha(vortxColors.teal, 0.1),
                                      color: vortxColors.teal,
                                      '&:hover': { bgcolor: alpha(vortxColors.teal, 0.2) },
                                    }}
                                  />
                                ) : '-'}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const rateName = rate.name || `${rate.utility_name} ${rate.tariff_code}`;
                                  const isSaved = approvedTariffs.some(t =>
                                    t.name === rateName ||
                                    (t.rate_schedule_name === rate.tariff_code &&
                                     t.rate_data?.voltage_level === rate.voltage_level &&
                                     t.rate_data?.option === rate.option)
                                  );
                                  return isSaved ? (
                                    <Chip label="Saved" size="small" sx={{ bgcolor: alpha('#2ecc71', 0.15), color: '#2ecc71', fontWeight: 500 }} />
                                  ) : (
                                    <Chip label="Pending" size="small" sx={{ bgcolor: alpha('#f39c12', 0.15), color: '#f39c12', fontWeight: 500 }} />
                                  );
                                })()}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Tooltip title="Edit">
                                    <IconButton size="small">
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const updated = extractedTariffData.filter((_, i) => i !== index);
                                        setExtractedTariffData(updated);
                                      }}
                                      sx={{ color: 'error.main', '&:hover': { bgcolor: alpha('#e74c3c', 0.1) } }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Divider sx={{ my: 3 }} />

                    {/* Save Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {extractedTariffData.filter(r => r._selected).length} of {extractedTariffData.length} selected
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => setExtractedTariffData(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          disabled={!extractedTariffData.some(r => r._selected)}
                          onClick={() => {
                            const remaining = extractedTariffData.filter(r => !r._selected);
                            if (remaining.length > 0) {
                              setExtractedTariffData(remaining);
                            } else {
                              setExtractedTariffData(null);
                            }
                          }}
                          startIcon={<DeleteIcon />}
                        >
                          Delete {extractedTariffData.filter(r => r._selected).length} Selected
                        </Button>
                        <Button
                          variant="contained"
                          disabled={!extractedTariffData.some(r => r._selected) || saving}
                          onClick={async () => {
                            const selectedRates = extractedTariffData.filter(r => r._selected);
                            if (selectedRates.length === 0) return;

                            setSaving(true);
                            try {
                              const token = await currentUser.getIdToken();
                              let savedCount = 0;

                              for (const data of selectedRates) {
                                const tariffPayload = {
                                  name: data.name || `${data.utility_name} ${data.tariff_code} ${data.voltage_level} ${data.option || ''}`.trim(),
                                  utility_name: data.utility_name,
                                  rate_schedule_name: data.tariff_code,
                                  tariff_type: 'electricity',
                                  rate_mode: 'tou',
                                  status: 'approved',
                                  demand_applicability: data.demand_applicability || null,
                                  rate_data: {
                                    voltage_level: data.voltage_level,
                                    option: data.option,
                                    demand_applicability: data.demand_applicability || null,
                                    seasons: data.seasons,
                                    periods: data.periods,
                                    windows: data.windows,
                                    schedule: data.schedule,
                                    fixedMonthlyCharge: data.fixedMonthlyFee,
                                    facilityDemand: data.facilityDemand,
                                    touData: {
                                      seasons: data.seasons,
                                      periods: data.periods,
                                      windows: data.windows,
                                      schedule: data.schedule,
                                      fixedMonthlyFee: data.fixedMonthlyFee,
                                      facilityDemand: data.facilityDemand
                                    }
                                  }
                                };

                                const response = await fetch(`${API_BASE_URL}/api/v1/admin/tariffs`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                  },
                                  body: JSON.stringify(tariffPayload)
                                });

                                if (response.ok) {
                                  savedCount++;
                                } else {
                                  console.error(`Failed to save: ${data.name}`);
                                }
                              }

                              setSuccess(`Saved ${savedCount} of ${selectedRates.length} tariffs!`);

                              // Remove saved tariffs from the list, keep unsaved ones for further editing
                              const remainingRates = extractedTariffData.filter(r => !r._selected);
                              if (remainingRates.length > 0) {
                                setExtractedTariffData(remainingRates);
                              } else {
                                // All tariffs were saved, clear the extraction view
                                setExtractedTariffData(null);
                                setMainTab(1);
                              }
                              fetchTariffs();
                            } catch (err) {
                              console.error('Failed to save tariffs:', err);
                              setError(err.message);
                            } finally {
                              setSaving(false);
                            }
                          }}
                          sx={{
                            bgcolor: '#2ecc71',
                            '&:hover': { bgcolor: '#27ae60' }
                          }}
                        >
                          {saving ? <CircularProgress size={20} /> : `Save ${extractedTariffData.filter(r => r._selected).length} Selected`}
                        </Button>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Raw JSON Collapsible */}
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', cursor: 'pointer' }}
                      onClick={(e) => {
                        const target = e.currentTarget.nextElementSibling;
                        if (target) target.style.display = target.style.display === 'none' ? 'block' : 'none';
                      }}
                    >
                      Show Raw JSON
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        display: 'none',
                        bgcolor: 'rgba(0,0,0,0.3)',
                        p: 2,
                        mt: 1,
                        borderRadius: 1,
                        overflow: 'auto',
                        maxHeight: 200,
                        fontSize: '0.7rem'
                      }}
                    >
                      {JSON.stringify(extractedTariffData, null, 2)}
                    </Box>
                  </Box>
                </Box>
              )}
        </TabPanel>
      )}

      {/* Wholesale Markets Tab - Super Admin Only */}
      {isSuperAdmin && (
        <TabPanel value={mainTab} index={4}>
          <WholesaleMarketsTab currentUser={currentUser} />
        </TabPanel>
      )}

      {/* Tariff Detail/Edit Dialog */}
      {renderTariffDialog()}

      {/* Create Tariff Dialog - Gas */}
      {tariffTypeTab === 'natural_gas' && (
        <GasTariffBuilder
          open={showCreateDialog}
          onSave={async (tariffData) => {
            try {
              const token = await currentUser.getIdToken();
              const response = await fetch(`${API_BASE_URL}/api/v1/admin/tariffs`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(tariffData)
              });

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to create tariff');
              }

              setShowCreateDialog(false);
              fetchTariffs();
            } catch (err) {
              console.error('Failed to create gas tariff:', err);
              setError(err.message);
            }
          }}
          onCancel={() => setShowCreateDialog(false)}
        />
      )}

      {/* Create Tariff Dialog - Electric */}
      {tariffTypeTab === 'electricity' && (
        <ElectricTariffBuilder
          open={showCreateDialog}
          initialData={extractedTariffData}
          onSave={async (tariffData) => {
            try {
              const token = await currentUser.getIdToken();
              const response = await fetch(`${API_BASE_URL}/api/v1/admin/tariffs`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(tariffData)
              });

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to create tariff');
              }

              setSuccess('Electric tariff created successfully');
              setShowCreateDialog(false);
              setExtractedTariffData(null); // Clear extracted data after save
              fetchTariffs();
            } catch (err) {
              console.error('Failed to create electric tariff:', err);
              setError(err.message);
            }
          }}
          onCancel={() => {
            setShowCreateDialog(false);
            setExtractedTariffData(null); // Clear extracted data on cancel
          }}
        />
      )}

    </AdminLayout>
  );
};

export default TariffManagementPage;
